import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import TitleSection from "../components/TitleSection/TitleSection";
import ContactFormPages from "../components/ContactFormPages";
import { Images } from "../assets";


const TimelineItemLeft = ({ project }) => (
  <div className="flex flex-col lg:grid lg:grid-cols-2 items-center gap-5 lg:items-start">
    {/* Image on the left */}
    <img src={Images[project.img]} alt={project.alt} className="w-full object-cover" />

    {/* Text on the right */}
    <div className="w-full h-full text-center lg:text-start">
      <h3 className="font-title text-6xl text-orange">{project.title}</h3>
      <h2 className="font-title uppercase text-orange text-2xl">
        {project.subtitle}
      </h2>
      {project.description.map((description, index) => (
        <div key={index} className="my-5">
          {Object.entries(description).map((content, index2) =>
            Array.isArray(content[1])
              ? content[1].map((text, index3) => (
                  <p
                    key={`${index}-${index2}-${index3}`}
                    className="text-white "
                  >
                    {text}
                  </p>
                ))
              : Object.entries(content[1]).map(([key, value], index3) => {
                  let renderedContent;

                  switch (key) {
                    case "link":
                      renderedContent = (
                        <div>
                          <span>{value.text}</span>
                          <a
                            href={value.link}
                            target="_blank"
                            rel="noreferrer"
                            className="underline text-blue-400"
                          >
                            {value.link}
                          </a>
                        </div>
                      );
                      break;

                    case "orange":
                      renderedContent = <p className="text-orange">{value}</p>;
                      break;

                    default:
                      renderedContent = <p>{value ? value : ""}</p>;
                  }

                  return (
                    <div key={`${index}-${index2}-${index3}`}>
                      {renderedContent}
                    </div>
                  );
                })
          )}
        </div>
      ))}
    </div>

    {/* Separator */}
    <div className="hidden lg:block col-span-2 h-1 translate-x-5 bg-gradient-to-l from-orange to-70% to-transparent w-full self-end" />
  </div>
);

const TimelineItemRight = ({ project }) => (
  <div className="flex flex-col lg:grid lg:grid-cols-2 items-center gap-5 lg:items-start">
    {/* Text on the left */}
    <div className="w-full text-center lg:text-end">
      <h3 className="font-title text-6xl text-orange mb-2 lg:mb-5">
        {project.title}
      </h3>
      <h2 className="font-title uppercase text-orange text-2xl">
        {project.subtitle}
      </h2>
      {project.description.map((description, index) => (
        <div key={index} className="my-5">
          {Object.entries(description).map((content, index2) =>
            Array.isArray(content[1])
              ? content[1].map((text, index3) => (
                  <p
                    key={`${index}-${index2}-${index3}`}
                    className="text-white "
                  >
                    {text}
                  </p>
                ))
              : Object.entries(content[1]).map(([key, value], index3) => {
                  let renderedContent;

                  switch (key) {
                    case "link":
                      renderedContent = (
                        <div>
                          <span>{value.text}</span>
                          <a
                            href={value.link}
                            target="_blank"
                            rel="noreferrer"
                            className="underline text-blue-400"
                          >
                            {value.link}
                          </a>
                        </div>
                      );
                      break;

                    case "orange":
                      renderedContent = <p className="text-orange">{value}</p>;
                      break;

                    default:
                      renderedContent = <p>{value ? value : ""}</p>;
                  }

                  return (
                    <div key={`${index}-${index2}-${index3}`}>
                      {renderedContent}
                    </div>
                  );
                })
          )}
        </div>
      ))}
    </div>

    {/* Image on the right */}
    <img src={Images[project.img]} alt={project.alt} className="w-full object-cover" />

    {/* Separator */}
    <div className="hidden lg:block col-span-2 h-1 -translate-x-5 bg-gradient-to-r from-orange to-70% to-transparent w-full self-end" />
  </div>
);

const Histoire = () => {
  const { t } = useTranslation();
  const arrowRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const sampleData = t("history.data", { returnObjects: true });


  // Observe the arrow head element to detect when it becomes visible (i.e. bottom reached)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsAtBottom(entry.isIntersecting);
        });
      },
      { threshold: 1 },
    );

    const currentArrow = arrowRef.current;
    if (currentArrow) observer.observe(currentArrow);

    return () => {
      if (currentArrow) observer.unobserve(currentArrow);
    };
  }, []);

  return (
    <>
      <TitleSection
        title={t("history.title")}
        subtitle={t("history.subtitle")}
        highlightWords={[t("history.highlightWords")]}
        bgImg={Images.US_BG_IMAGE}
      />

      <section className="relative w-full py-20 px-5 lg:px-10">
        {/* Mobile Layout: items stack vertically */}
        <div className="lg:hidden space-y-28">
          {sampleData.map((project, index) => (
            <div key={index}>
              <TimelineItemLeft project={project} />
            </div>
          ))}
        </div>

        {/* Desktop Layout: three-column grid */}
        <div className="hidden lg:block">
          {/* Vertical Timeline Tail */}
          <div
            className="relative sticky left-[42.5%] top-0 h-[calc(100vh+6em)] w-[15%] rounded-t-lg z-10"
            style={{
              clipPath:
                "polygon(0 0, 100% 0, 100% calc(100% - 5em), 50% 100%, 0 calc(100% - 5em))",
            }}
          >
            {/* Bottom gradient (default) */}
            <div className="absolute inset-0 bg-gradient-to-b from-orange to-black rounded-t-lg" />

            {/* Top gradient (overlay) with opacity transition */}
            <div
              className={`absolute inset-0 bg-gradient-to-b from-orange to-orange rounded-t-lg transition-opacity duration-1000 ease-in-out ${
                isAtBottom ? "opacity-100" : "opacity-0"
              }`}
            />
          </div>

          <div className="grid grid-cols-[41.5%_17%_41.5%] -mt-[100vh]">
            {sampleData.map((project, index) => (
              <React.Fragment key={index}>
                {index % 2 === 0 ? (
                  // Even rows: item appears on the left column.
                  <>
                    <div className="col-start-1 py-32">
                      <TimelineItemLeft project={project} />
                    </div>
                    <div className="col-start-2"></div>
                    <div className="col-start-3 py-32"></div>
                  </>
                ) : (
                  // Odd rows: item appears on the right column.
                  <>
                    <div className="col-start-1 py-32"></div>
                    <div className="col-start-2"></div>
                    <div className="col-start-3 py-32">
                      <TimelineItemRight project={project} />
                    </div>
                  </>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div ref={arrowRef}></div>
      </section>

      <ContactFormPages />
    </>
  );
};

export default Histoire;
